// packages/pulse-sdk/src/context.tsx
// ─────────────────────────────────────────────────────────────────────────────
// <PulseProvider> — the root the host app wraps its PULSE-backed surfaces in.
// Owns:
//   • the in-memory token accessor (getToken) + the fetch client
//   • a TanStack QueryClient (provides one if the host doesn't already have one)
//
// The provider never persists the token; it calls the host's getToken() per
// request. When the identity broker ships (Phase 4) the host swaps its getToken
// implementation and nothing here changes.
// ─────────────────────────────────────────────────────────────────────────────

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { PulseClient, type GetToken } from "./client";
import type { AuthInfo } from "./types";

export interface PulseContextValue {
  client: PulseClient;
  /** Synchronous best-effort auth info derived from the most recent token. */
  auth: AuthInfo;
  /** Re-read the token and refresh the derived auth info. */
  refreshAuth: () => Promise<void>;
}

const PulseContext = createContext<PulseContextValue | null>(null);

export interface PulseProviderProps {
  apiBaseUrl: string;
  getToken: GetToken;
  /** Optional fetch override (tests / SSR). */
  fetchImpl?: typeof fetch;
  /**
   * Provide a QueryClient if the host already has one. When omitted, the
   * provider creates and owns one. Pass `queryClient={null}` is not supported —
   * omit the prop to get the default.
   */
  queryClient?: QueryClient;
  children: ReactNode;
}

const DEFAULT_QUERY_OPTIONS = {
  queries: {
    retry: 1,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  },
} as const;

export function PulseProvider(props: PulseProviderProps) {
  const { apiBaseUrl, getToken, fetchImpl, children } = props;

  // Own a QueryClient only if the host didn't supply one. Created once.
  const [ownedClient] = useState(() =>
    props.queryClient ? null : new QueryClient({ defaultOptions: DEFAULT_QUERY_OPTIONS })
  );
  const queryClient = props.queryClient ?? ownedClient!;

  const [auth, setAuth] = useState<AuthInfo>({ user: null, scopes: [] });

  // The client + refreshAuth depend only on the wiring props, never on `auth`.
  // Keeping `auth` out of these deps is what prevents a render loop: refreshAuth
  // calls setAuth, and if it were a dep the memo would rebuild and re-fire
  // refreshAuth forever (decodeAuth returns a fresh object each call).
  const client = useMemo(
    () => new PulseClient({ apiBaseUrl, getToken, fetchImpl }),
    [apiBaseUrl, getToken, fetchImpl]
  );

  const refreshAuth = useCallback(async () => {
    const token = await getToken();
    const next = decodeAuth(token);
    // Skip the state update when nothing changed, so a stable token doesn't
    // churn re-renders.
    setAuth((prev) => (authEqual(prev, next) ? prev : next));
  }, [getToken]);

  // Seed auth once on mount and whenever the token accessor changes.
  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  const value = useMemo<PulseContextValue>(
    () => ({ client, auth, refreshAuth }),
    [client, auth, refreshAuth]
  );

  const inner = <PulseContext.Provider value={value}>{children}</PulseContext.Provider>;

  // If the host already wraps the tree in its own QueryClientProvider, nesting a
  // second one with the SAME client is a no-op; with our owned client it wires
  // the SDK hooks. Either way this is safe.
  return <QueryClientProvider client={queryClient}>{inner}</QueryClientProvider>;
}

export function usePulse(): PulseContextValue {
  const ctx = useContext(PulseContext);
  if (!ctx) {
    throw new Error("usePulse must be used within <PulseProvider>");
  }
  return ctx;
}

export function usePulseClient(): PulseClient {
  return usePulse().client;
}

// Re-export so consumers/tests can grab the underlying query client if needed.
export { useQueryClient };

// ── Token decode (best-effort, no verification) ───────────────────────────────
// The SDK does NOT verify the token (the server does). We only read the public
// claims to surface { user, scopes } for UI gating. A malformed token yields an
// empty AuthInfo rather than throwing.
export function decodeAuth(token: string | null): AuthInfo {
  if (!token) return { user: null, scopes: [] };
  const parts = token.split(".");
  if (parts.length < 2) return { user: null, scopes: [] };
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]!)) as {
      sub?: string;
      scope?: string;
    };
    const scopes = (payload.scope ?? "").split(/\s+/).filter(Boolean);
    return { user: payload.sub ?? null, scopes };
  } catch {
    return { user: null, scopes: [] };
  }
}

function authEqual(a: AuthInfo, b: AuthInfo): boolean {
  return (
    a.user === b.user &&
    a.scopes.length === b.scopes.length &&
    a.scopes.every((s, i) => s === b.scopes[i])
  );
}

function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const normalized = b64 + pad;
  if (typeof atob === "function") {
    return decodeURIComponent(
      atob(normalized)
        .split("")
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
  }
  // Node fallback (SSR / tests without atob). Referenced via globalThis so the
  // package typechecks without @types/node in DOM-only consumers.
  const nodeBuffer = (globalThis as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } })
    .Buffer;
  if (nodeBuffer) return nodeBuffer.from(normalized, "base64").toString("utf-8");
  return "";
}
